import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import type { Invoice, Currency } from '@hero/schema';
import { getInvoiceAmountToPay } from '../invoice/useInvoiceInfo';

/**
 * Payment mode determines the flow shape:
 * - invoice → pay pre-selected invoice(s), skip to upload
 * - select  → show invoice selector, then checkout
 * - advance → enter amount, then upload (no invoices)
 */
export type PaymentMode = 'invoice' | 'select' | 'advance';

/**
 * Steps in the checkout flow:
 * - select_invoices → user picks invoices (mode='select')
 * - enter_amount    → user enters deposit amount (mode='advance')
 * - upload          → user uploads bank transfer receipt
 * - success         → confirmation screen
 */
export type CheckoutStep = 'select_invoices' | 'enter_amount' | 'upload' | 'success';

export interface CheckoutCashback {
  /** Cashback percentage as decimal (e.g. 0.02 for 2%) */
  percentage: number;
  /** Days remaining for the cashback offer */
  daysLeft?: number;
  /**
   * Hard floor — the absolute minimum deposit amount.
   * Deposits below this are rejected (user cannot proceed to checkout).
   */
  minAdvanceDeposit?: number;
  /**
   * Minimum deposit to qualify for cashback.
   * User can still deposit >= `minAdvanceDeposit`, but cashback
   * is only awarded when the amount meets this higher threshold.
   */
  cashbackMinDeposit?: number;
}

export interface InvoiceCashbackInfo {
  /** Cashback percentage as decimal (e.g. 0.02 for 2%) */
  percentage: number;
  /** Days left for the cashback offer */
  daysLeft?: number;
}

type PaymentCheckoutStore = {
  // ---- State ----
  /** Whether the payment drawer is open */
  isOpen: boolean;
  /** Current payment mode */
  mode: PaymentMode;
  /** Current step in the flow */
  step: CheckoutStep;
  /** Invoices selected for payment */
  selectedInvoices: Invoice[];
  /** Additional advance deposit amount (undefined = not entered) */
  advanceAmount: number | undefined;
  /** Uploaded proof-of-payment file */
  file: File | null;
  /** Whether the payment is being submitted */
  isSubmitting: boolean;
  /** Currency for this checkout session */
  currency: Currency;
  /** Cashback context (from the triggering action) */
  cashback: CheckoutCashback | null;

  // ---- Actions ----
  /** Open the payment flow and initialise state */
  open: (params: {
    mode: PaymentMode;
    invoices?: Invoice[];
    currency?: Currency;
    cashback?: CheckoutCashback | null;
  }) => void;
  /** Close the payment drawer and reset state */
  close: () => void;
  /** Navigate to a specific step */
  setStep: (step: CheckoutStep) => void;
  /** Replace the selected invoices list */
  setSelectedInvoices: (invoices: Invoice[]) => void;
  /** Toggle an invoice in/out of the selection. Pass the full Invoice object. */
  toggleInvoiceSelection: (invoice: Invoice) => void;
  /** Set the advance deposit amount */
  setAdvanceAmount: (amount: number | undefined) => void;
  /** Set the uploaded file */
  setFile: (file: File | null) => void;
  /** Set the submitting flag */
  setIsSubmitting: (submitting: boolean) => void;

  // ---- Derived helpers (computed from state) ----
  /** Sum of selected invoice remaining amounts */
  getInvoiceTotal: () => number;
  /** Grand total = invoiceTotal + advanceAmount */
  getTotal: () => number;
  /** Whether this checkout has invoices */
  hasInvoices: () => boolean;
  /** Whether this is a hybrid payment (invoices + advance) */
  isHybrid: () => boolean;
  /** Whether this is advance-only (no invoices) */
  isAdvanceOnly: () => boolean;
  /** Whether the current advance amount qualifies for cashback */
  isCashbackQualified: () => boolean;
};

const getInitialStep = (mode: PaymentMode): CheckoutStep => {
  switch (mode) {
    case 'invoice':
      return 'upload';
    case 'select':
      return 'select_invoices';
    case 'advance':
      return 'enter_amount';
  }
};

const INITIAL_STATE = {
  isOpen: false,
  mode: 'select' as PaymentMode,
  step: 'select_invoices' as CheckoutStep,
  selectedInvoices: [] as Invoice[],
  advanceAmount: undefined as number | undefined,
  file: null as File | null,
  isSubmitting: false,
  currency: 'SAR' as Currency,
  cashback: null as CheckoutCashback | null,
};

/** Timer for delayed state reset after close animation */
let closeResetTimer: ReturnType<typeof setTimeout> | null = null;
const CLOSE_ANIMATION_MS = 350;

export const usePaymentCheckoutStore = create<PaymentCheckoutStore>()(
  immer((set, get) => ({
    ...INITIAL_STATE,

    // ---- Actions ----
    open: ({ mode, invoices = [], currency = 'SAR' as Currency, cashback = null }) => {
      if (closeResetTimer) { clearTimeout(closeResetTimer); closeResetTimer = null; }
      set(state => {
        state.isOpen = true;
        state.mode = mode;
        state.step = getInitialStep(mode);
        state.selectedInvoices = mode === 'invoice' ? invoices : [];
        state.advanceAmount = undefined;
        state.file = null;
        state.isSubmitting = false;
        state.currency = currency;
        state.cashback = cashback;
      });
    },

    close: () => {
      if (closeResetTimer) clearTimeout(closeResetTimer);
      set(state => { state.isOpen = false; });
      closeResetTimer = setTimeout(() => {
        set(state => { Object.assign(state, INITIAL_STATE); });
        closeResetTimer = null;
      }, CLOSE_ANIMATION_MS);
    },

    setStep: step =>
      set(state => {
        state.step = step;
      }),

    setSelectedInvoices: invoices =>
      set(state => {
        state.selectedInvoices = invoices;
      }),

    toggleInvoiceSelection: invoice =>
      set(state => {
        const idx = state.selectedInvoices.findIndex(
          inv => String(inv._id) === String(invoice._id),
        );
        if (idx >= 0) {
          state.selectedInvoices.splice(idx, 1);
        } else {
          state.selectedInvoices.push(invoice);
        }
      }),

    setAdvanceAmount: amount =>
      set(state => {
        state.advanceAmount = amount;
      }),

    setFile: file =>
      set(state => {
        state.file = file;
      }),

    setIsSubmitting: submitting =>
      set(state => {
        state.isSubmitting = submitting;
      }),

    // ---- Derived helpers ----
    getInvoiceTotal: () => {
      const { selectedInvoices } = get();
      return selectedInvoices.reduce(
        (sum, inv) => sum + getInvoiceAmountToPay(inv),
        0,
      );
    },

    getTotal: () => {
      const { advanceAmount } = get();
      return get().getInvoiceTotal() + (advanceAmount ?? 0);
    },

    hasInvoices: () => get().selectedInvoices.length > 0,

    isHybrid: () => get().hasInvoices() && (get().advanceAmount ?? 0) > 0,

    isAdvanceOnly: () => !get().hasInvoices() && (get().advanceAmount ?? 0) > 0,

    isCashbackQualified: () => {
      const { advanceAmount, cashback } = get();
      if (!cashback || !cashback.cashbackMinDeposit) return false;
      return (advanceAmount ?? 0) >= cashback.cashbackMinDeposit;
    },
  })),
);
